// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import FormField, {
    FormFieldComponentProps,
    FormFieldPresentationProps,
    FormFieldSizeVariant,
} from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { applyPropBuilder, getProp, injectCss, resolveColor, safeColor } from "@insite/mobius/utilities";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import XngExpirationDatePickerInput, {
    ExpirationDatePlaceholders,
    ExpirationDateValue,
} from "@werner/App/Components/XngExpirationDatePicker/PickerInput";
import { HasAvailableUnits, YearFormat } from "@werner/App/Components/XngExpirationDatePicker/utils/types";
import { chevronLeftString, chevronRightString } from "@werner/MobiusOverrides/DatePicker/DatePicker";
import { sizeVariantValues } from "@werner/MobiusOverrides/FormField/formStyles";
import { fontConfig } from "@werner/XDL/PreStyle/typography";
import { color } from "@werner/XDL/Utilities";
import React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

interface XngExpirationDatePickerProps
    extends ThemeProps<BaseTheme>,
        Partial<
            Pick<
                FormFieldComponentProps,
                "disabled" | "error" | "hint" | "label" | "labelPosition" | "labelId" | "required"
            >
        > {
    /**
     * Unique id to be passed into the input element.
     * If not provided, a random id is assigned (an id is required for accessibility purposes).
     */
    uid?: number | string;
    /**
     * fired on a valid value change
     * @param pickerState the current state of the picker
     */
    onDateChange?(pickerState: XngExpirationDatePickerState): void;
    /** Adds an asterisk to the input's label (if provided). Additionally, governs whether an error if the date
     * field is empty */
    required?: boolean;
    /**
     * the selected / picked date
     */
    selectedDate?: ExpirationDateValue<number>;
    /**
     * month & year placeholders
     */
    placeholders?: ExpirationDatePlaceholders;
    /**
     * whether the year 4-digits (2022) or 2-digits (22)
     */
    yearFormat?: YearFormat;
    /**
     * month input aria-label
     */
    monthAriaLabel?: string;
    /**
     * year input aria-label
     */
    yearAriaLabel?: string;
    /**
     * to keep the calendar open all the time
     */
    keepCalendarOpen?: boolean;
    /**
     * month format numbers (01, 02, ... 12) or names (Jan ... Dec)
     */
    monthFormat?: "numbers" | "names";
}

export interface XngExpirationDatePickerPresentationProps
    extends FormFieldPresentationProps<XngExpirationDatePickerProps> {
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable
     */
    cssOverrides?: {
        datePicker?: StyledProp<XngExpirationDatePickerProps>;
        descriptionWrapper?: StyledProp<XngExpirationDatePickerProps>;
        formField?: StyledProp<XngExpirationDatePickerProps>;
        formInputWrapper?: StyledProp<XngExpirationDatePickerProps>;
        inputSelect?: StyledProp<XngExpirationDatePickerProps>;
    };
    /** Props to be passed into the calendarIcon component.
     * @themable */
    calendarIconProps?: IconPresentationProps;
    /** Background color of the form element */
    backgroundColor?: string;
}

enum XngExpirationDatePickerPopupDirection {
    Right = "Right",
    Left = "Left",
}

export interface XngExpirationDatePickerState {
    selectedDate?: ExpirationDateValue<number>;
    isEmpty: boolean;
    mobilePopupDirection?: XngExpirationDatePickerPopupDirection;
    windowWidth?: number;
}

type Props = XngExpirationDatePickerProps &
    HasAvailableUnits &
    XngExpirationDatePickerPresentationProps &
    HasDisablerContext &
    ThemeProps<BaseTheme>;

interface XngExpirationDatePickerStyleProps extends ThemeProps<BaseTheme> {
    _sizeVariant: FormFieldSizeVariant;
    css?: StyledProp;
    disabled: boolean;
    mobilePopupDirection?: XngExpirationDatePickerPopupDirection;
}

export type DatePickerProps = XngExpirationDatePickerPresentationProps & XngExpirationDatePickerProps;

const DatePickerIcon = styled(Icon)<
    { _sizeVariant: FormFieldSizeVariant; _backgroundColor: string } & ThemeProps<BaseTheme>
>`
    width: ${({ _sizeVariant, size }) => size ?? sizeVariantValues[_sizeVariant].height - 4}px;
    height: ${({ _sizeVariant, size }) => size ?? sizeVariantValues[_sizeVariant].height - 4}px;
    padding: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].iconPadding - 2}px;
    background: ${({ theme, _backgroundColor }) => resolveColor(_backgroundColor, theme)};
    border-radius: 100%;
`;

export const XngExpirationDatePickerStyle = styled.div<XngExpirationDatePickerStyleProps>`
    button:focus {
        /* outline is added on span for border reasons */
        outline: none;
        span {
            outline-color: ${getProp("theme.focus.color", "#09f")};
            outline-style: ${getProp("theme.focus.style", "solid")};
            outline-width: ${getProp("theme.focus.width", "2px")};
        }
    }
    .react-datetime-picker {
        color: ${({ disabled, theme }) => (disabled ? theme.colors.text.disabled : theme.colors.text.main)};
        display: inline-flex;
        position: relative;
        &,
        & *,
        & *::before,
        & *::after {
            box-sizing: border-box;
        }
        &__wrapper {
            display: flex;
            flex-grow: 1;
            flex-shrink: 0;
        }
        &__inputGroup {
            /* own padding + inputs padding + digits width + dots width */
            min-width: calc(4px + (4px * 3) + 1.2em * 6 + 0.217em * 2);
            flex-grow: 1;
            &__divider {
                padding: 1px 0;
                white-space: pre;
            }
            &__input {
                /* outline removed as the parent 'input' provides the focus indicator. */
                outline: none;
                min-width: 1.2em;
                height: calc(100% - 2px);
                position: relative;
                border: 0;
                background: none;
                font: inherit;
                box-sizing: content-box;
                appearance: textfield;
                padding: 1px 2px;
                &::-webkit-outer-spin-button,
                &::-webkit-inner-spin-button {
                    appearance: none;
                    margin: 0;
                }
            }
            select {
                appearance: menuList;
            }
            &__amPm {
                font: inherit;
            }
            &__leadingZero {
                margin: -1px -2px 0 0;
            }
        }

        &__button {
            border: 0;
            background: transparent;
            padding: 0;
            &:enabled {
                cursor: pointer;
                z-index: 3;
            }
        }
        &__calendar {
            top: 100% !important;
            z-index: ${getProp("theme.zIndex.datePicker")};
            width: 0;

            &--closed {
                display: none;
            }
        }
    }
    .react-calendar {
        position: absolute;
        ${({ mobilePopupDirection }) =>
            mobilePopupDirection === XngExpirationDatePickerPopupDirection.Left
                ? css`
                      right: 0;
                      left: unset;
                  `
                : css`
                      left: 0;
                      right: unset;
                  `}
        background: ${getColor("common.background")};
        z-index: ${getProp("theme.zIndex.datePicker")};
        margin-top: 2px;
        box-shadow: 0 0 15px 0 ${color("primary", 0.15)};
        top: 100%;
        font-family: inherit;
        padding: 20px 16px;
        flex-direction: row;
        user-select: none;

        &,
        & *,
        & *::before,
        & *::after {
            box-sizing: border-box;
        }

        button {
            background-color: ${getColor("common.background")};
            &:enabled {
                &:hover:not(:disabled),
                &:focus:not(:disabled) {
                    color: ${getColor("common.ui3")};
                    background-color: ${props => safeColor(props.theme.colors.common.ui2).rgb().alpha(0.4).string()};
                    cursor: pointer;
                }
            }
        }

        &__navigation {
            display: flex;
            margin-bottom: 20px;

            button {
                min-width: 40px;
                border: none;
                cursor: pointer;
            }
            &__arrow {
                background-position: center center;
                background-repeat: no-repeat;
                width: 35px;
                height: 30px;
                font-size: 0;
                padding: 4px;
                margin: 2px;
            }
            &__next-button {
                background-image: url("${`data:image/svg+xml;base64,${chevronRightString}`}");
            }
            &__prev-button {
                background-image: url("${`data:image/svg+xml;base64,${chevronLeftString}`}");
            }
            &__prev2-button,
            &__next2-button {
                display: none;
            }
            &__label {
                width: 160px;
                color: ${getColor("text.main")};
                font-size: 16px;
                line-height: 18px;
                letter-spacing: 0.5px;
            }
        }

        &__month-view {
            &__weekdays {
                font-size: 10px;
                font-weight: 900;
                letter-spacing: 0.41px;
                line-height: 14px;
                color: ${getColor("text.main")};
                text-align: center;
                margin-bottom: 8px;
                abbr {
                    text-decoration: none;
                }
            }

            &__days {
                &__day {
                    border: none;
                    border-radius: 3px;
                    cursor: pointer;
                    font-size: 12px;
                    letter-spacing: 0;
                    line-height: 26px;
                    padding: 0;
                    margin: 0;
                    color: ${getColor("common.ui3")};
                    font-family: ${fontConfig.fontFamily};
                    &:disabled {
                        color: ${getColor("common.ui4")};
                        cursor: default;
                    }
                    &--neighboringMonth {
                        color: ${getColor("primary")};
                        text-decoration: underline;
                    }
                }
            }
        }

        &__year-view,
        &__decade-view,
        &__century-view {
            .react-calendar__tile {
                padding: 1em 1px;
                background: none;
                border: none;
                color: ${color("common.ui3")};
            }
        }

        &__tile {
            &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
            &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                color: ${getColor("primary")};
                text-decoration: underline;
            }
            &--now {
                color: ${getColor("common.ui3")};
            }
        }
        &__year-view__months {
            display: flex;
            flex-wrap: wrap;
        }
        &__decade-view__years {
            display: flex;
            flex-wrap: wrap;
        }
        &__navigation {
            display: flex;
        }
    }

    ${injectCss}
`;

class XngExpirationDatePicker extends React.Component<Props, XngExpirationDatePickerState> {
    private readonly datePickerRef = React.createRef<HTMLDivElement>();
    state: XngExpirationDatePickerState = {
        isEmpty: false,
        windowWidth: typeof window !== "undefined" ? window.innerWidth : 0,
    };

    handleResize = () => {
        this.setState({ windowWidth: window.innerWidth });
    };

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }
    handleChange = (value: ExpirationDateValue<number>) => {
        this.setState(
            {
                isEmpty: !(value && value.month && value.year),
                selectedDate: value,
            },
            () => {
                if (typeof this.props.onDateChange === "function") {
                    this.props.onDateChange?.(this.state);
                }
            },
        );
    };
    render() {
        const {
            disable,
            disabled,
            error,
            label,
            required,
            theme: { translate },
            backgroundColor,
            availableMonths,
            availableYears,
            placeholders,
            yearFormat,
            monthAriaLabel,
            yearAriaLabel,
            keepCalendarOpen,
            monthFormat,
        } = this.props;
        const { isEmpty } = this.state;
        const isDisabled = !!(disable || disabled);
        const { applyProp, spreadProps } = applyPropBuilder(this.props, {
            component: "datePicker",
            category: "formField",
        });
        const inputId = this.props.uid || uniqueId();
        const descriptionId = `${inputId}-description`;
        const { datePicker: datePickerCss, ..._cssOverrides } = spreadProps("cssOverrides");
        const calendarIconProps = spreadProps("calendarIconProps");
        const sizeVariant: FormFieldSizeVariant = applyProp("sizeVariant", "default");
        const windowWidth = this.state.windowWidth ?? 0;

        const pickerInput = (
            <XngExpirationDatePickerStyle
                _sizeVariant={sizeVariant}
                css={datePickerCss}
                disabled={isDisabled}
                mobilePopupDirection={this.state.mobilePopupDirection}
                aria-invalid={(required && isEmpty) || false}
            >
                <XngExpirationDatePickerInput
                    onChange={this.handleChange}
                    value={this.props.selectedDate}
                    disabled={isDisabled}
                    availableMonths={availableMonths}
                    availableYears={availableYears}
                    placeholders={placeholders}
                    yearFormat={yearFormat}
                    id={`${inputId}`}
                    ariaLabel={typeof label === "string" ? label : "expiration date Input"}
                    monthAriaLabel={monthAriaLabel}
                    yearAriaLabel={yearAriaLabel}
                    keepCalendarOpen={keepCalendarOpen}
                    monthFormat={monthFormat}
                    onCalendarOpen={() => {
                        const { right, left } = this.datePickerRef.current!.getBoundingClientRect();
                        const rightOffset = Math.abs(windowWidth - right);

                        this.setState({
                            mobilePopupDirection:
                                rightOffset > left
                                    ? XngExpirationDatePickerPopupDirection.Right
                                    : XngExpirationDatePickerPopupDirection.Left,
                        });
                    }}
                    calendarIcon={
                        <>
                            <DatePickerIcon
                                _sizeVariant={sizeVariant}
                                {...calendarIconProps}
                                _backgroundColor={backgroundColor}
                                color={isDisabled ? "text.disabled" : calendarIconProps.color}
                            />
                            <VisuallyHidden>{translate("open or close calendar")}</VisuallyHidden>
                        </>
                    }
                />
            </XngExpirationDatePickerStyle>
        );
        return (
            <StyledWrapper
                as="div"
                css={css`
                    width: 100%;
                `}
                ref={this.datePickerRef}
            >
                <FormField
                    descriptionId={descriptionId}
                    formInput={pickerInput}
                    inputId={inputId}
                    cssOverrides={_cssOverrides}
                    backgroundColor={backgroundColor}
                    error={required && isEmpty ? error || translate("please choose a valid date") : null}
                    {...this.props}
                />
            </StyledWrapper>
        );
    }
}

export default withDisabler(withTheme(XngExpirationDatePicker));
